import { useCssVars as _useCssVars, unref as _unref, defineComponent as _defineComponent } from 'vue'
import { vShow as _vShow, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, withDirectives as _withDirectives, renderSlot as _renderSlot, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';

const SWIPE_PX = 250;


export default /*@__PURE__*/_defineComponent({
  __name: 'SwipeRefresh',
  props: {
    refresh: {},
    allowSwipe: { type: Boolean, default: true },
    signal: {},
    match: { default: '' }
  },
  setup(__props: any) {

_useCssVars(_ctx => ({
  "v6ab5b97c": (progress.value)
}))

const props = __props;

/** Is active interaction */
const on = ref(false);
/** Start touch Y coordinate */
const startY = ref(0);
/** End touch Y coordinate */
const endY = ref(0);
/** Start touch X coordinate */
const startX = ref(0);
/** End touch X coordinate */
const endX = ref(0);
/** Percentage progress to show in swiping */
const progress = ref(0);
/** Next update frame reference */
const updateFrame = ref(0);

// Loading animation state
const loading = ref(false);
const animate = ref(false);
const wasSwiped = ref(true);
const firstcycle = ref(0);

onMounted(() => {
  animate.value = loading.value; // start if needed
  props.signal?.addEventListener('abort', reset, { once: true });
});

onBeforeUnmount(() => {
  props.signal?.removeEventListener('abort', reset);
  reset();
});

// A new signal means a new parent generation: drop any in-progress
// gesture and progress tied to the old one, so stale coordinates
// can't trigger a refresh or leave the progress bar stuck.
watch(
  () => props.signal,
  (signal, prev) => {
    prev?.removeEventListener('abort', reset);
    signal?.addEventListener('abort', reset, { once: true });
    reset();
  },
);

watch(loading, () => {
  wasSwiped.value = progress.value >= 100;
  if (!wasSwiped.value) {
    // The loading animation was triggered from elsewhere
    // let it continue normally
    animate.value = loading.value;
    return;
  }

  // Let the animation run for at least half cycle
  // if the user pulled down, so we provide good feedback
  // that something actually happened
  if (loading.value) {
    if (!animate.value) {
      firstcycle.value = window.setTimeout(() => {
        firstcycle.value = 0;
        animate.value = loading.value;
      }, 750);
    }
    animate.value = loading.value;
  } else {
    if (!firstcycle.value) {
      animate.value = loading.value;
    }
  }
});

const show = computed(() => {
  return (on.value && progress.value) || animate.value;
});

function reset() {
  // Clear events
  window.cancelAnimationFrame(updateFrame.value);
  window.clearTimeout(firstcycle.value);

  // Reset state
  on.value = false;
  progress.value = 0;
  updateFrame.value = 0;
  loading.value = false;
  animate.value = false;
  wasSwiped.value = true;
  firstcycle.value = 0;
}

/** Start gesture on container (passive) */
function touchstart(event: TouchEvent) {
  if (!props.allowSwipe) return;
  if (event.touches.length !== 1) return;
  const touch = event.touches[0];

  // Check if top element matches selector
  if (props.match && !(<HTMLElement>touch.target).closest(props.match)) return;

  // Start swipe action
  endY.value = startY.value = touch.clientY;
  endX.value = startX.value = touch.clientX;
  progress.value = 0;
  on.value = true;
}

/** Execute gesture on container (passive) */
function touchmove(event: TouchEvent) {
  if (!props.allowSwipe || !on.value) return;

  // Ignore multi-touch gestures (e.g. pinch zoom)
  if (event.touches.length !== 1) {
    return reset();
  }

  // Get the touch coordinates
  const touch = event.touches[0];
  endY.value = touch.clientY;
  endX.value = touch.clientX;

  // Abort on mostly-horizontal gestures (e.g. swipe right)
  if (Math.abs(endX.value - startX.value) > Math.abs(endY.value - startY.value)) {
    return reset();
  }

  // Update progress only once per frame
  updateFrame.value ||= window.requestAnimationFrame(async () => {
    updateFrame.value = 0;
    if (!on.value) return;

    // Re-check horizontal dominance with latest coordinates
    if (Math.abs(endX.value - startX.value) > Math.abs(endY.value - startY.value)) {
      return reset();
    }

    // Compute percentage of swipe
    const delta = (endY.value - startY.value) / SWIPE_PX;
    progress.value = Math.min(Math.max(0, delta * 100), 100);

    // Execute action on threshold
    if (progress.value >= 100) {
      on.value = false;
      const signal = props.signal;
      try {
        loading.value = true;
        await props.refresh();
      } finally {
        if (signal !== props.signal || !signal?.aborted) {
          loading.value = false;
        }
      }
    }
  });
}

/** End gesture on container (passive) */
function touchend(event: TouchEvent) {
  on.value = false;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    onTouchstartPassive: touchstart,
    onTouchmovePassive: touchmove,
    onTouchendPassive: touchend
  }, [
    _withDirectives(_createElementVNode("div", {
      class: _normalizeClass(["swipe-progress", { animate: animate.value, wasSwiped: wasSwiped.value }])
    }, null, 2), [
      [_vShow, show.value]
    ]),
    _renderSlot(_ctx.$slots, "default")
  ], 32))
}
}

})